import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container, Typography, Box, Grid,
  TextField, Button, Chip, Avatar, Rating, Dialog,
  DialogTitle, DialogContent, DialogActions, Divider,
  Alert, IconButton, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import SearchIcon       from '@mui/icons-material/Search';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import AccessTimeIcon   from '@mui/icons-material/AccessTime';
import AddIcon          from '@mui/icons-material/Add';
import RemoveIcon       from '@mui/icons-material/Remove';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SmokeFreeIcon    from '@mui/icons-material/SmokeFree';
import MusicNoteIcon    from '@mui/icons-material/MusicNote';
import PetsIcon         from '@mui/icons-material/Pets';
import VerifiedIcon     from '@mui/icons-material/Verified';
import GroupIcon        from '@mui/icons-material/Group';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import Co2Icon          from '@mui/icons-material/Co2';
import EmojiPeopleIcon  from '@mui/icons-material/EmojiPeople';
import AttachMoneyIcon  from '@mui/icons-material/AttachMoney';
import PersonAddIcon    from '@mui/icons-material/PersonAdd';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import carpoolService   from '../../services/carpool.service';
import { useAuth }      from '../../context/AuthContext';
import { TZ, todayMayotte } from '../../utils/date';
import MapboxAddressInput from '../../components/MapboxAddressInput';

/* ── Design tokens ─────────────────────────────────────────── */
const T = {
  purple: '#552583',
  purpleDark: '#3d1a61',
  gold: '#FDB927',
  bg: '#E4F0F6',
  white: '#ffffff',
  ink: '#1a1a1a',
  sub: '#4a4a4a',
  muted: '#7a7a7a',
  border: 'rgba(85,37,131,0.08)',
  radius: { sm: '8px', md: '10px', btn: '12px', card: '20px' },
  transition: '0.15s ease',
  shadow: '0 1px 4px rgba(0,0,0,0.06)',
};

const btnPrimary = {
  bgcolor: T.purple, color: 'white', fontWeight: 700,
  borderRadius: T.radius.btn, textTransform: 'none',
  transition: T.transition, boxShadow: 'none',
  '&:hover': { bgcolor: T.purpleDark, boxShadow: 'none' },
};


/* ── Input style for search bar on bg ──────────────────────── */
const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: T.white, color: T.ink, borderRadius: T.radius.md,
    border: `1px solid ${T.border}`,
    transition: T.transition,
    '& fieldset': { border: 'none' },
    '&:hover': { borderColor: 'rgba(85,37,131,0.18)' },
    '&.Mui-focused': { borderColor: T.purple },
  },
  '& input': { color: T.ink, fontWeight: 600 },
  '& .MuiInputLabel-root': { color: T.muted },
};

const mapboxInputSx = {
  '& .MuiTypography-root': { color: T.muted },
  '& .MuiInputBase-root': {
    background: T.white,
    border: `1px solid ${T.border}`,
    borderRadius: T.radius.md,
    transition: T.transition,
    '&:hover': { borderColor: 'rgba(85,37,131,0.18)' },
  },
  '& input': { color: T.ink, fontWeight: 600, '&::placeholder': { color: `${T.muted} !important` } },
};

const CarpoolSearch = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [searchParams, setSearchParams] = useState({
    departure_city: '', arrival_city: '', date: todayMayotte(), seats_needed: 1,
  });
  const [carpools, setCarpools]               = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [searched, setSearched]               = useState(false);
  const [bookingDialog, setBookingDialog]     = useState(false);
  const [selectedCarpool, setSelectedCarpool] = useState(null);
  const [bookingSeats, setBookingSeats]       = useState(1);
  const [bookingError, setBookingError]       = useState('');
  const [bookingLoading, setBookingLoading]   = useState(false);
  const [sortBy, setSortBy]                   = useState('time');

  const handleSearch = async () => {
    setLoading(true); setSearched(true);
    try {
      const data = await carpoolService.search(searchParams);
      setCarpools(data.carpools || []);
    } catch { setCarpools([]); }
    finally { setLoading(false); }
  };

  const handleBooking = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setBookingError(''); setBookingLoading(true);
    try {
      await carpoolService.createBooking({
        carpool_id: selectedCarpool.id, booking_date: searchParams.date,
        seats_booked: bookingSeats, pickup_location: selectedCarpool.departure_address,
        dropoff_location: selectedCarpool.arrival_address,
      });
      setBookingDialog(false); navigate('/carpool/bookings');
    } catch (error) {
      setBookingError(error.response?.data?.message || 'Réservation impossible, veuillez réessayer.');
    } finally { setBookingLoading(false); }
  };

  const parsePreferences = (pref) => {
    if (!pref) return [];
    if (Array.isArray(pref)) return pref;
    return pref.split(',').map(p => p.trim()).filter(Boolean);
  };

  const getPreferenceIcon = (pref) => {
    const p = pref.toLowerCase();
    if (p.includes('fumeur'))  return <SmokeFreeIcon sx={{ fontSize: 13 }} />;
    if (p.includes('musique')) return <MusicNoteIcon sx={{ fontSize: 13 }} />;
    if (p.includes('animaux')) return <PetsIcon sx={{ fontSize: 13 }} />;
    return null;
  };

  const sorted = [...carpools].sort((a, b) =>
    sortBy === 'price' ? a.price_per_seat - b.price_per_seat
      : (a.departure_time || '').localeCompare(b.departure_time || '')
  );

  return (
    <Box sx={{ bgcolor: T.bg, minHeight: '100vh', pb: 8 }}>

      {/* ── HERO ── */}
      <Box sx={{ bgcolor: T.purple, pt: { xs: 5, md: 7 }, pb: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>

          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography sx={{
              color: 'rgba(255,255,255,0.45)', fontWeight: 600,
              letterSpacing: '0.12em', fontSize: '0.65rem', mb: 1.5, textTransform: 'uppercase',
            }}>
              COVOITURAGE
            </Typography>
            <Typography sx={{
              fontWeight: 800, color: 'white', mb: 1.5,
              fontSize: { xs: '1.8rem', md: '2.6rem' }, lineHeight: 1.1,
              letterSpacing: '-0.02em',
            }}>
              Partagez la route,{' '}
              <Box component="span" sx={{ color: T.gold }}>partagez les frais</Box>
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', maxWidth: 420, mx: 'auto', fontSize: '0.9rem', lineHeight: 1.6 }}>
              Trouvez un trajet ou proposez le vôtre. Économique, écologique et convivial.
            </Typography>

            {/* Stats */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 3.5, md: 6 }, mt: 4 }}>
              {[
                { icon: <SavingsOutlinedIcon />, value: '-70%', label: 'vs taxi' },
                { icon: <Co2Icon />,             value: '-45%', label: 'CO2 émis' },
                { icon: <EmojiPeopleIcon />,     value: '2 000+', label: 'trajets/mois' },
              ].map((s, i) => (
                <Box key={i} sx={{ textAlign: 'center' }}>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.4} mb={0.2}>
                    <Box sx={{ color: 'rgba(255,255,255,0.4)', '& svg': { fontSize: 15 } }}>{s.icon}</Box>
                    <Typography sx={{ fontWeight: 800, color: 'white', fontSize: '1.05rem' }}>{s.value}</Typography>
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.65rem' }}>{s.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* ── SEARCH BAR ── */}
          <Box sx={{
            maxWidth: 900, mx: 'auto', bgcolor: T.bg,
            borderRadius: T.radius.card, border: `1px solid rgba(85,37,131,0.06)`,
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', p: { xs: 2.5, md: 3 },
          }}>
            <Grid container spacing={1.5} alignItems="flex-end">

              <Grid item xs={12} sm={6} md={3}>
                <MapboxAddressInput
                  label="Départ" placeholder="Ville de départ..."
                  value={searchParams.departure_city}
                  onChange={(text) => setSearchParams(p => ({ ...p, departure_city: text }))}
                  onSelect={(result) => {
                    if (!result) { setSearchParams(p => ({ ...p, departure_city: '' })); return; }
                    setSearchParams(p => ({ ...p, departure_city: result.city || result.shortAddress || result.address }));
                  }}
                  showGeolocate iconColor={T.purple} sx={mapboxInputSx}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <MapboxAddressInput
                  label="Destination" placeholder="Ville d'arrivée..."
                  value={searchParams.arrival_city}
                  onChange={(text) => setSearchParams(p => ({ ...p, arrival_city: text }))}
                  onSelect={(result) => {
                    if (!result) { setSearchParams(p => ({ ...p, arrival_city: '' })); return; }
                    setSearchParams(p => ({ ...p, arrival_city: result.city || result.shortAddress || result.address }));
                  }}
                  showGeolocate={false} iconColor="#e74c3c" sx={mapboxInputSx}
                />
              </Grid>

              <Grid item xs={6} sm={4} md={2.5}>
                <Typography sx={{ color: T.muted, fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.6, display: 'block' }}>
                  Date
                </Typography>
                <TextField fullWidth type="date"
                  value={searchParams.date}
                  onChange={e => setSearchParams(p => ({ ...p, date: e.target.value }))}
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={6} sm={3} md={1.5}>
                <Typography sx={{ color: T.muted, fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.6, display: 'block' }}>
                  Places
                </Typography>
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  bgcolor: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius.md,
                  px: 0.8, height: 40,
                }}>
                  <IconButton size="small" onClick={() => setSearchParams(p => ({ ...p, seats_needed: Math.max(1, p.seats_needed - 1) }))}
                    sx={{ color: T.muted, p: 0.4, '&:hover': { color: T.purple } }}>
                    <RemoveIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                  <Typography sx={{ color: T.ink, fontWeight: 700, fontSize: '0.95rem' }}>
                    {searchParams.seats_needed}
                  </Typography>
                  <IconButton size="small" onClick={() => setSearchParams(p => ({ ...p, seats_needed: Math.min(8, p.seats_needed + 1) }))}
                    sx={{ color: T.muted, p: 0.4, '&:hover': { color: T.purple } }}>
                    <AddIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
              </Grid>

              <Grid item xs={12} sm={5} md={2}>
                <Button fullWidth variant="contained" onClick={handleSearch} disabled={loading}
                  startIcon={!loading && <SearchIcon sx={{ fontSize: '1rem !important' }} />}
                  sx={{ ...btnPrimary, height: 40, fontSize: '0.88rem',
                    '&:disabled': { bgcolor: 'rgba(85,37,131,0.15)', color: T.muted },
                  }}>
                  {loading ? 'Recherche...' : 'Rechercher'}
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ borderColor: T.border, my: 2 }} />

            <Box sx={{ textAlign: 'center' }}>
              <Button component={Link} to="/carpool/create" startIcon={<AddIcon sx={{ fontSize: '0.9rem !important' }} />}
                sx={{ color: T.muted, textTransform: 'none', fontWeight: 500, fontSize: '0.82rem', borderRadius: T.radius.sm, transition: T.transition, '&:hover': { color: T.purple, bgcolor: 'rgba(85,37,131,0.04)' } }}>
                Vous conduisez ? Proposez votre trajet
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── RESULTS SECTION ── */}
      <Container maxWidth="lg" sx={{ mt: -3, position: 'relative', zIndex: 2 }}>

        {/* Skeleton */}
        {loading && (
          <Grid container spacing={2}>
            {[1, 2, 3].map(i => (
              <Grid item xs={12} key={i}>
                <Box sx={{ opacity: 1 - i * 0.25, bgcolor: T.white, borderRadius: T.radius.btn, border: `1px solid ${T.border}`, p: 2.5 }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: 'rgba(85,37,131,0.06)', flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ height: 12, borderRadius: 1, bgcolor: 'rgba(85,37,131,0.06)', mb: 1.2, width: '50%' }} />
                      <Box sx={{ height: 9, borderRadius: 1, bgcolor: 'rgba(85,37,131,0.04)', width: '30%' }} />
                    </Box>
                    <Box sx={{ width: 80, height: 36, borderRadius: T.radius.sm, bgcolor: 'rgba(85,37,131,0.06)' }} />
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Before search: how it works */}
        {!loading && !searched && (
          <Box>
            <Typography sx={{ color: T.muted, fontWeight: 600, letterSpacing: '0.1em', fontSize: '0.62rem', textTransform: 'uppercase', mb: 2 }}>
              Comment ça marche
            </Typography>
            <Grid container spacing={2} sx={{ mb: 5 }}>
              {[
                { step: '01', title: 'Cherchez', desc: 'Entrez votre départ, destination et date souhaitée.' },
                { step: '02', title: 'Choisissez', desc: 'Comparez les trajets, les conducteurs et les prix.' },
                { step: '03', title: 'Réservez', desc: 'Confirmez en quelques secondes et retrouvez le conducteur.' },
              ].map(s => (
                <Grid item xs={12} sm={4} key={s.step}>
                  <Box sx={{
                    p: 2.5, height: '100%', bgcolor: T.white,
                    borderRadius: T.radius.btn, border: `1px solid ${T.border}`,
                    boxShadow: T.shadow, transition: T.transition,
                    '&:hover': { borderColor: 'rgba(85,37,131,0.15)' },
                  }}>
                    <Typography sx={{ fontWeight: 800, color: 'rgba(85,37,131,0.1)', fontSize: '2.5rem', lineHeight: 1, mb: 1 }}>{s.step}</Typography>
                    <Typography sx={{ fontWeight: 700, color: T.ink, mb: 0.3, fontSize: '0.92rem' }}>{s.title}</Typography>
                    <Typography sx={{ color: T.sub, fontSize: '0.82rem', lineHeight: 1.5 }}>{s.desc}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* No results */}
        {!loading && searched && sorted.length === 0 && (
          <Box sx={{ bgcolor: T.white, borderRadius: T.radius.card, border: `1px solid ${T.border}`, boxShadow: T.shadow }}>
            <Box sx={{ textAlign: 'center', py: 7, px: 3 }}>
              <DirectionsCarIcon sx={{ fontSize: 56, color: 'rgba(85,37,131,0.12)', mb: 2 }} />
              <Typography sx={{ fontWeight: 700, color: T.sub, mb: 0.5, fontSize: '1rem' }}>Aucun trajet trouvé</Typography>
              <Typography sx={{ color: T.muted, mb: 3, maxWidth: 360, mx: 'auto', fontSize: '0.85rem' }}>
                Essayez d'autres villes ou une autre date, ou proposez ce trajet vous-même.
              </Typography>
              <Box display="flex" gap={1.5} justifyContent="center" flexWrap="wrap">
                <Button onClick={() => setSearched(false)} variant="outlined"
                  sx={{ borderRadius: T.radius.btn, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', borderColor: T.border, color: T.sub, transition: T.transition, '&:hover': { borderColor: T.purple, color: T.purple } }}>
                  Modifier la recherche
                </Button>
                <Button component={Link} to="/carpool/create" variant="contained" startIcon={<AddIcon />}
                  sx={{ ...btnPrimary, fontSize: '0.85rem' }}>
                  Proposer ce trajet
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Results */}
        {!loading && searched && sorted.length > 0 && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2.5 }} flexDirection={{ xs: 'column', sm: 'row' }} gap={1.5}>
              <Box>
                <Typography sx={{ fontWeight: 700, color: T.ink, fontSize: '1.1rem' }}>
                  {sorted.length} trajet{sorted.length > 1 ? 's' : ''} trouvé{sorted.length > 1 ? 's' : ''}
                </Typography>
                {(searchParams.departure_city || searchParams.arrival_city) && (
                  <Typography sx={{ color: T.muted, mt: 0.2, fontSize: '0.82rem' }}>
                    {[searchParams.departure_city, searchParams.arrival_city].filter(Boolean).join(' -> ')}
                    {' -- '}{new Date(searchParams.date).toLocaleDateString('fr-FR', { timeZone: TZ, weekday: 'short', day: 'numeric', month: 'long' })}
                  </Typography>
                )}
              </Box>
              <ToggleButtonGroup value={sortBy} exclusive onChange={(_, v) => v && setSortBy(v)} size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    border: `1px solid ${T.border}`, color: T.muted,
                    textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', px: 1.5,
                    transition: T.transition, borderRadius: `${T.radius.sm} !important`,
                    '&.Mui-selected': { bgcolor: 'rgba(85,37,131,0.06)', color: T.purple, borderColor: 'rgba(85,37,131,0.2)' },
                    '&:hover': { bgcolor: 'rgba(85,37,131,0.03)' },
                  },
                }}>
                <ToggleButton value="time"><AccessTimeIcon sx={{ fontSize: 14, mr: 0.5 }} />Heure</ToggleButton>
                <ToggleButton value="price"><AttachMoneyIcon sx={{ fontSize: 14, mr: 0.5 }} />Prix</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Grid container spacing={1.5}>
              {sorted.map(trip => (
                <Grid item xs={12} key={trip.id}>
                  <Box sx={{
                    bgcolor: T.white, borderRadius: T.radius.btn, border: `1px solid ${T.border}`,
                    boxShadow: T.shadow, p: { xs: 2, md: 2.5 }, transition: T.transition,
                    '&:hover': { borderColor: 'rgba(85,37,131,0.15)', boxShadow: '0 3px 12px rgba(0,0,0,0.08)' },
                  }}>
                    <Grid container spacing={2} alignItems="center">

                      {/* Driver */}
                      <Grid item xs={12} sm={2.5}>
                        <Box display="flex" alignItems="center" gap={1.2}>
                          <Box sx={{ position: 'relative' }}>
                            <Avatar sx={{
                              width: 42, height: 42, bgcolor: 'rgba(85,37,131,0.08)',
                              color: T.purple, border: `1px solid ${T.border}`,
                              fontWeight: 700, fontSize: '1rem',
                            }}>
                              {trip.first_name?.charAt(0)}
                            </Avatar>
                            {trip.is_verified && (
                              <VerifiedIcon sx={{ position: 'absolute', bottom: -2, right: -2, fontSize: 14, color: '#3498db' }} />
                            )}
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 600, color: T.ink, fontSize: '0.88rem' }}>
                              {trip.first_name}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={0.4}>
                              <Rating value={Number(trip.rating) || 0} size="small" readOnly precision={0.1}
                                sx={{ '& .MuiRating-iconFilled': { color: T.gold }, fontSize: '0.7rem' }} />
                              <Typography sx={{ color: T.muted, fontSize: '0.65rem' }}>
                                ({trip.total_ratings || 0})
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>

                      {/* Route */}
                      <Grid item xs={12} sm={5.5}>
                        <Box display="flex" gap={1.5} alignItems="stretch">
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.3, flexShrink: 0 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: T.purple }} />
                            <Box sx={{ width: 1.5, flex: 1, bgcolor: 'rgba(85,37,131,0.1)', my: 0.4, minHeight: 18 }} />
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#e74c3c' }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ mb: 1.2 }}>
                              <Typography sx={{ fontWeight: 600, color: T.ink, fontSize: '0.85rem' }}>
                                {trip.departure_address}
                              </Typography>
                              <Box display="flex" alignItems="center" gap={0.4} mt={0.15}>
                                <AccessTimeIcon sx={{ fontSize: 11, color: T.muted }} />
                                <Typography sx={{ color: T.sub, fontWeight: 600, fontSize: '0.72rem' }}>
                                  {trip.departure_time}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography sx={{ fontWeight: 500, color: T.sub, fontSize: '0.85rem' }}>
                              {trip.arrival_address}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Tags */}
                        <Box display="flex" gap={0.6} mt={1.2} flexWrap="wrap">
                          {(trip.vehicle_info || (trip.vehicle_brand && trip.vehicle_model)) && (
                            <Chip icon={<DirectionsCarIcon sx={{ fontSize: 12 }} />}
                              label={trip.vehicle_info || `${trip.vehicle_brand} ${trip.vehicle_model}`}
                              size="small"
                              sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(85,37,131,0.04)', color: T.purple, border: `1px solid ${T.border}`, '& .MuiChip-icon': { color: T.purple } }}
                            />
                          )}
                          {parsePreferences(trip.preferences).map((pref, i) => (
                            <Chip key={i} icon={getPreferenceIcon(pref)} label={pref} size="small"
                              sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(85,37,131,0.03)', color: T.sub, border: `1px solid ${T.border}`, '& .MuiChip-icon': { color: T.sub } }}
                            />
                          ))}
                        </Box>
                      </Grid>

                      {/* Price + action */}
                      <Grid item xs={12} sm={4}>
                        <Box display="flex" alignItems={{ xs: 'center', sm: 'flex-end' }}
                          flexDirection={{ xs: 'row', sm: 'column' }}
                          justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
                          gap={1.2} sx={{ textAlign: { sm: 'right' } }}>
                          <Box>
                            <Typography sx={{ fontWeight: 800, color: T.purple, lineHeight: 1, fontSize: { xs: '1.5rem', md: '1.7rem' } }}>
                              {trip.price_per_seat} EUR
                            </Typography>
                            <Typography sx={{ color: T.muted, fontSize: '0.68rem' }}>par place</Typography>
                          </Box>

                          <Box display="flex" alignItems="center" gap={0.8}>
                            <Chip icon={<AirlineSeatReclineNormalIcon sx={{ fontSize: 13 }} />}
                              label={`${trip.remaining_seats} place${trip.remaining_seats > 1 ? 's' : ''}`}
                              size="small"
                              sx={{
                                bgcolor: trip.remaining_seats <= 1 ? 'rgba(231,76,60,0.06)' : 'rgba(85,37,131,0.04)',
                                color: trip.remaining_seats <= 1 ? '#e74c3c' : T.purple,
                                border: `1px solid ${trip.remaining_seats <= 1 ? 'rgba(231,76,60,0.15)' : T.border}`,
                                fontWeight: 600, fontSize: '0.7rem', height: 24,
                                '& .MuiChip-icon': { color: 'inherit' },
                              }}
                            />
                            <Button variant="contained"
                              disabled={trip.remaining_seats === 0}
                              onClick={() => { setSelectedCarpool(trip); setBookingSeats(1); setBookingError(''); setBookingDialog(true); }}
                              sx={{ ...btnPrimary, px: 2, py: 0.6, fontSize: '0.82rem' }}>
                              Réserver
                            </Button>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* ── CTA CARDS ── */}
        {!loading && (
          <Grid container spacing={2} sx={{ mt: 5 }}>
            <Grid item xs={12} sm={6}>
              <Box sx={{
                p: { xs: 2.5, md: 3 }, height: '100%', bgcolor: T.white,
                borderRadius: T.radius.btn, border: `1px solid ${T.border}`,
                boxShadow: T.shadow, display: 'flex', flexDirection: 'column',
                transition: T.transition, '&:hover': { borderColor: 'rgba(85,37,131,0.15)' },
              }}>
                <Box sx={{ width: 38, height: 38, borderRadius: T.radius.md, bgcolor: 'rgba(85,37,131,0.05)', border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                  <GroupIcon sx={{ color: T.purple, fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 700, color: T.ink, mb: 0.5, fontSize: '0.95rem' }}>Je cherche un trajet</Typography>
                <Typography sx={{ color: T.sub, mb: 2.5, lineHeight: 1.5, flex: 1, fontSize: '0.82rem' }}>
                  Trouvez facilement un covoitureur qui fait le même trajet et économisez sur vos déplacements.
                </Typography>
                <Button component={Link} to="/carpool/bookings" variant="outlined"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: '0.85rem !important' }} />}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: T.radius.btn, alignSelf: 'flex-start', fontSize: '0.82rem', borderColor: T.border, color: T.sub, transition: T.transition, '&:hover': { borderColor: T.purple, color: T.purple } }}>
                  Mes réservations
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{
                p: { xs: 2.5, md: 3 }, height: '100%', bgcolor: T.white,
                borderRadius: T.radius.btn, border: `1px solid rgba(85,37,131,0.12)`,
                boxShadow: T.shadow, display: 'flex', flexDirection: 'column',
                transition: T.transition, '&:hover': { borderColor: 'rgba(85,37,131,0.2)' },
              }}>
                <Box sx={{ width: 38, height: 38, borderRadius: T.radius.md, bgcolor: 'rgba(85,37,131,0.05)', border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                  <PersonAddIcon sx={{ color: T.purple, fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 700, color: T.ink, mb: 0.5, fontSize: '0.95rem' }}>Je propose un trajet</Typography>
                <Typography sx={{ color: T.sub, mb: 2.5, lineHeight: 1.5, flex: 1, fontSize: '0.82rem' }}>
                  Rentabilisez vos trajets quotidiens en prenant des passagers. Partagez les frais d'essence.
                </Typography>
                <Button component={Link} to="/carpool/create" variant="contained" startIcon={<AddIcon />}
                  endIcon={<ArrowForwardIcon sx={{ fontSize: '0.85rem !important' }} />}
                  sx={{ ...btnPrimary, alignSelf: 'flex-start', fontSize: '0.82rem' }}>
                  Proposer un trajet
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
      </Container>

      {/* ── BOOKING DIALOG ── */}
      <Dialog open={bookingDialog} onClose={() => !bookingLoading && setBookingDialog(false)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: T.radius.card, bgcolor: T.white } }}>

        <DialogTitle sx={{ fontWeight: 700, color: T.ink, fontSize: '1.05rem', pb: 0.5 }}>
          Confirmer la réservation
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {bookingError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: T.radius.sm }} onClose={() => setBookingError('')}>
              {bookingError}
            </Alert>
          )}

          {selectedCarpool && (
            <Box>
              <Box sx={{ p: 2, borderRadius: T.radius.btn, bgcolor: 'rgba(85,37,131,0.03)', border: `1px solid ${T.border}`, mb: 2.5 }}>
                <Box display="flex" alignItems="center" gap={1.2} mb={2}>
                  <Avatar sx={{ width: 38, height: 38, bgcolor: 'rgba(85,37,131,0.08)', color: T.purple, fontWeight: 700 }}>
                    {selectedCarpool.first_name?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: T.ink, fontSize: '0.88rem' }}>{selectedCarpool.first_name}</Typography>
                    <Rating value={Number(selectedCarpool.rating) || 0} size="small" readOnly precision={0.1}
                      sx={{ '& .MuiRating-iconFilled': { color: T.gold }, fontSize: '0.65rem' }} />
                  </Box>
                </Box>

                <Box display="flex" gap={1.2} alignItems="stretch">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.3, flexShrink: 0 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: T.purple }} />
                    <Box sx={{ width: 1.5, height: 22, bgcolor: 'rgba(85,37,131,0.1)', my: 0.4 }} />
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#e74c3c' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography sx={{ fontWeight: 600, color: T.ink, fontSize: '0.85rem' }}>{selectedCarpool.departure_address}</Typography>
                      <Typography sx={{ color: T.muted, fontSize: '0.72rem' }}>{selectedCarpool.departure_time}</Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 500, color: T.sub, fontSize: '0.85rem' }}>{selectedCarpool.arrival_address}</Typography>
                  </Box>
                </Box>
              </Box>

              <Typography sx={{ color: T.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6rem', mb: 1 }}>
                Nombre de places
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(85,37,131,0.03)', border: `1px solid ${T.border}`, borderRadius: T.radius.btn, px: 1.5, py: 1 }}>
                <IconButton size="small" onClick={() => setBookingSeats(s => Math.max(1, s - 1))}
                  sx={{ bgcolor: 'rgba(85,37,131,0.06)', color: T.purple, '&:hover': { bgcolor: 'rgba(85,37,131,0.12)' } }}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 800, color: T.purple, fontSize: '1.2rem', lineHeight: 1 }}>{bookingSeats}</Typography>
                  <Typography sx={{ color: T.muted, fontSize: '0.65rem' }}>
                    place{bookingSeats > 1 ? 's' : ''} -- max {selectedCarpool.remaining_seats}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setBookingSeats(s => Math.min(selectedCarpool.remaining_seats, s + 1))}
                  sx={{ bgcolor: 'rgba(85,37,131,0.06)', color: T.purple, '&:hover': { bgcolor: 'rgba(85,37,131,0.12)' } }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>

              <Divider sx={{ my: 2.5, borderColor: T.border }} />

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography sx={{ color: T.sub, fontSize: '0.85rem' }}>
                  {bookingSeats} place{bookingSeats > 1 ? 's' : ''} x {selectedCarpool.price_per_seat} EUR
                </Typography>
                <Typography sx={{ fontWeight: 800, color: T.purple, fontSize: '1.4rem' }}>
                  {selectedCarpool.price_per_seat * bookingSeats} EUR
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0, gap: 1 }}>
          <Button onClick={() => setBookingDialog(false)} disabled={bookingLoading}
            sx={{ color: T.muted, textTransform: 'none', fontWeight: 500, borderRadius: T.radius.sm, fontSize: '0.85rem' }}>
            Annuler
          </Button>
          <Button onClick={handleBooking} variant="contained" disabled={bookingLoading}
            sx={{ ...btnPrimary, px: 3, fontSize: '0.85rem' }}>
            {bookingLoading ? 'Réservation...' : `Confirmer -- ${selectedCarpool ? selectedCarpool.price_per_seat * bookingSeats : 0} EUR`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CarpoolSearch;
